import { useMutation, useQuery } from '@tanstack/react-query'
import { createRaynetClient, CreateLeadPayload } from '@/api/raynetClient'
import { RaynetCategory } from '@/api/adminClient'

// Lazy initialize client only when needed
const getRaynetClient = () => {
  try {
    return createRaynetClient()
  } catch (error) {
    console.warn('Raynet klient není dostupný:', error)
    return null
  }
}

// Funkce pro uložení historie leadu
const saveLeadHistory = async (
  raynetId: number | null,
  leadData: Record<string, unknown>,
  status: 'success' | 'error',
  errorMessage?: string
) => {
  try {
    const response = await fetch('/api/leads', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        raynet_id: raynetId,
        lead_data: leadData,
        status,
        error_message: errorMessage,
      }),
    })
    
    if (!response.ok) {
      console.error('Chyba při ukládání historie leadu:', response.status, response.statusText)
    } else {
      console.log('📋 Historie leadu uložena do databáze')
    }
  } catch (error) {
    console.error('Chyba při ukládání historie leadu:', error)
  }
}

// Funkce pro uložení requestu do offline fronty
const saveToOfflineQueue = async (payload: CreateLeadPayload) => {
  try {
    const response = await fetch('/api/offline-queue', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        request_type: 'lead',
        payload,
      }),
    })
    
    if (!response.ok) {
      throw new Error('Chyba při ukládání do offline fronty')
    }
    
    const result = await response.json()
    console.log('📥 Request uložen do offline fronty s ID:', result.queueId)
    return result.queueId
  } catch (error) {
    console.error('Chyba při ukládání do offline fronty:', error)
    throw error
  }
}

// Hook pro načítání konfigurace
export function useKioskConfig() {
  return useQuery({
    queryKey: ['kiosk-config'],
    queryFn: async () => {
      const [configResponse, categoriesResponse] = await Promise.all([
        fetch('/api/kiosk-config'),
        fetch('/api/public/categories')
      ])
      
      if (!configResponse.ok) {
        throw new Error('Chyba při načítání konfigurace kiosku')
      }
      
      const config = await configResponse.json()
      const categories: RaynetCategory[] = categoriesResponse.ok ? await categoriesResponse.json() : []
      
      return {
        leadTopic: config.lead_topic || '',
        leadSource: config.lead_source ? parseInt(config.lead_source) : undefined,
        leadCategory: config.lead_category ? parseInt(config.lead_category) : undefined,
        availableCategories: categories.filter((cat) => cat.valid) || [],
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useCreateLead() {
  const { data: config } = useKioskConfig()
  
  return useMutation({
    mutationFn: async (data: Omit<CreateLeadPayload, 'sourceId' | 'topic'>) => {
      const client = getRaynetClient()
      if (!client) {
        throw new Error('Raynet API není nakonfigurované')
      }
      
      // Přidáme konfiguraci ze serveru
      const payload: CreateLeadPayload = {
        ...data,
        sourceId: config?.leadSource,
        topic: config?.leadTopic,
      }
      
      console.log('🎯 Spouštím vytvoření leadu:')
      console.log('📋 Formulářová data:', JSON.stringify(data, null, 2))
      console.log('⚙️ Konfigurace kiosku:', JSON.stringify(config, null, 2))
      console.log('📦 Finální payload:', JSON.stringify(payload, null, 2))
      
      return client.createLead(payload)
    },
    onSuccess: async (result, variables) => {
      console.log('🎉 Lead úspěšně vytvořen!')
      console.log('📊 Výsledek:', JSON.stringify(result, null, 2))
      
      // Uložíme historii úspěšného leadu
      const leadData = {
        ...variables,
        sourceId: config?.leadSource,
        topic: config?.leadTopic,
        raynet_response: result,
      }
      
      await saveLeadHistory(result.id, leadData, 'success')
    },
    onError: async (error: Error, variables) => {
      console.error('💥 Chyba při vytváření leadu:', error)
      console.error('📋 Data která způsobila chybu:', JSON.stringify(variables, null, 2))
      
      // Připravíme finální payload pro offline frontu
      const payload: CreateLeadPayload = {
        ...variables,
        sourceId: config?.leadSource,
        topic: config?.leadTopic,
      }
      
      try {
        // Pokusíme se uložit do offline fronty
        await saveToOfflineQueue(payload)
        
        // Uložíme historii s poznámkou o offline fronty (převedeme přes JSON)
        const payloadRecord = JSON.parse(JSON.stringify(payload)) as Record<string, unknown>
        await saveLeadHistory(null, payloadRecord, 'error', 'Lead uložen do offline fronty - ' + error.message)
        
        // Upravíme error message pro uživatele
        throw new Error('Vaše údaje byly uloženy pro pozdější zpracování. Požadavek bude odeslán jakmile bude Raynet API dostupné.')
      } catch (offlineError) {
        console.error('💥 Chyba při ukládání do offline fronty:', offlineError)
        
        // Uložíme historii původní chyby (převedeme přes JSON)
        const payloadRecord = JSON.parse(JSON.stringify(payload)) as Record<string, unknown>
        await saveLeadHistory(null, payloadRecord, 'error', error.message)
        
        // Pošleme původní chybu pokud se nepovedlo ani uložit do fronty
        throw error
      }
    },
  })
}

export function useLeads() {
  return useQuery({
    queryKey: ['leads'],
    queryFn: async () => {
      const client = getRaynetClient()
      if (!client) {
        throw new Error('Raynet API není nakonfigurované')
      }
      return client.getLeads()
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: false, // Disable auto-fetch for admin panel
  })
} 